import React, { useState } from 'react';
import { Phone, Mail, MessageSquare, Clock, PhoneCall, PhoneMissed, CheckCircle } from 'lucide-react';
import { EditableComment } from './leads/EditableComment';

const LeadActionLog = ({ leadId, leadStatus, actionLog = [], onActionAdded }) => {
  const [activeTab, setActiveTab] = useState('log_call');
  const [callNotes, setCallNotes] = useState('');
  const [comment, setComment] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Check if lead is existing customer
  const isExistingCustomer = leadStatus === 'existing_customer';

  const handleLogCall = async () => {
    if (!callNotes.trim()) {
      alert('Please enter call notes');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/leads/${leadId}/actions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'call',
          notes: callNotes
        })
      });

      if (response.ok) {
        setCallNotes('');
        onActionAdded();
      } else {
        alert('Failed to log call');
      }
    } catch (error) {
      console.error('Error logging call:', error);
      alert('Error logging call');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkEmailed = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/leads/${leadId}/actions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'email_sent'
        })
      });

      if (response.ok) {
        onActionAdded();
      } else {
        alert('Failed to mark as emailed');
      }
    } catch (error) {
      console.error('Error marking emailed:', error);
      alert('Error marking as emailed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      alert('Please enter a comment');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/leads/${leadId}/actions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'comment',
          text: comment
        })
      });

      if (response.ok) {
        setComment('');
        onActionAdded();
      } else {
        alert('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskAction = async (taskType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/leads/${leadId}/actions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: taskType,
          notes: taskNotes.trim() || undefined
        })
      });

      if (response.ok) {
        setTaskNotes('');
        onActionAdded();
      } else {
        alert(`Failed to log ${taskType}`);
      }
    } catch (error) {
      console.error(`Error logging ${taskType}:`, error);
      alert(`Error logging ${taskType}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'call':
        return <Phone className="w-4 h-4" />;
      case 'email_sent':
        return <Mail className="w-4 h-4" />;
      case 'comment':
        return <MessageSquare className="w-4 h-4" />;
      case 'called':
        return <PhoneCall className="w-4 h-4" />;
      case 'left_voicemail':
        return <PhoneMissed className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'call':
        return 'bg-blue-100 text-blue-800';
      case 'email_sent':
        return 'bg-green-100 text-green-800';
      case 'comment':
        return 'bg-purple-100 text-purple-800';
      case 'called':
        return 'bg-cyan-100 text-cyan-800';
      case 'left_voicemail':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-4">Staff Actions</h3>
      
      {/* Action Tabs */}
      <div className="flex border-b border-gray-200 mb-4 flex-wrap">
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'log_call'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('log_call')}
        >
          <Phone className="w-4 h-4 inline mr-1" />
          Log Call
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'mark_emailed'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('mark_emailed')}
        >
          <Mail className="w-4 h-4 inline mr-1" />
          Mark Emailed
        </button>
        <button
          className={`px-4 py-2 font-medium text-sm ${
            activeTab === 'add_comment'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('add_comment')}
        >
          <MessageSquare className="w-4 h-4 inline mr-1" />
          Add Comment
        </button>
        
        {/* Additional tabs for existing customer status */}
        {isExistingCustomer && (
          <>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'called'
                  ? 'border-b-2 border-cyan-500 text-cyan-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('called')}
            >
              <PhoneCall className="w-4 h-4 inline mr-1" />
              Called
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'left_voicemail'
                  ? 'border-b-2 border-orange-500 text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('left_voicemail')}
            >
              <PhoneMissed className="w-4 h-4 inline mr-1" />
              Left Voicemail
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'completed'
                  ? 'border-b-2 border-emerald-500 text-emerald-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('completed')}
            >
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Mark Completed
            </button>
          </>
        )}
      </div>

      {/* Action Forms */}
      <div className="mb-6">
        {activeTab === 'log_call' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Call Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Enter details about the call..."
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
            />
            <button
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              onClick={handleLogCall}
              disabled={loading}
            >
              {loading ? 'Logging...' : 'Log Call'}
            </button>
          </div>
        )}

        {activeTab === 'mark_emailed' && (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Mark that you've sent an email to this lead. The date and time will be logged automatically.
            </p>
            <button
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              onClick={handleMarkEmailed}
              disabled={loading}
            >
              {loading ? 'Marking...' : 'Mark as Emailed'}
            </button>
          </div>
        )}

        {activeTab === 'add_comment' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="3"
              placeholder="Add notes or comments about this lead..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              onClick={handleAddComment}
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Comment'}
            </button>
          </div>
        )}

        {/* Existing Customer Task Actions */}
        {isExistingCustomer && activeTab === 'called' && (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Mark that you've successfully reached and spoke with this existing customer.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              rows="2"
              placeholder="Add any notes about the conversation..."
              value={taskNotes}
              onChange={(e) => setTaskNotes(e.target.value)}
            />
            <button
              className="mt-3 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              onClick={() => handleTaskAction('called')}
              disabled={loading}
            >
              {loading ? 'Logging...' : 'Mark as Called'}
            </button>
          </div>
        )}

        {isExistingCustomer && activeTab === 'left_voicemail' && (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Mark that you've left a voicemail for this existing customer.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              rows="2"
              placeholder="Add any notes about the voicemail..."
              value={taskNotes}
              onChange={(e) => setTaskNotes(e.target.value)}
            />
            <button
              className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              onClick={() => handleTaskAction('left_voicemail')}
              disabled={loading}
            >
              {loading ? 'Logging...' : 'Mark as Left Voicemail'}
            </button>
          </div>
        )}

        {isExistingCustomer && activeTab === 'completed' && (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Mark this existing customer follow-up as completed. This will update the lead status.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Completion Notes (Optional)
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              rows="2"
              placeholder="Add any final notes about the follow-up..."
              value={taskNotes}
              onChange={(e) => setTaskNotes(e.target.value)}
            />
            <button
              className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              onClick={() => handleTaskAction('completed')}
              disabled={loading}
            >
              {loading ? 'Completing...' : 'Mark as Completed'}
            </button>
          </div>
        )}
      </div>

      {/* Action History */}
      <div>
        <h4 className="text-md font-semibold mb-3">Action History</h4>
        {actionLog.length === 0 ? (
          <p className="text-gray-500 text-sm">No actions logged yet</p>
        ) : (
          <div className="space-y-3">
            {actionLog.slice().reverse().map((action, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-full ${getActionColor(action.type)}`}>
                  {getActionIcon(action.type)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm capitalize">
                      {action.type === 'email_sent' ? 'Email Sent' : 
                       action.type === 'left_voicemail' ? 'Left Voicemail' :
                       action.type.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(action.date)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    By: {action.user_name || action.user_email}
                  </p>
                  {action.type === 'comment' ? (
                    <EditableComment key={action.date + (action.edited_at || '')} leadId={leadId} action={action} onSaved={onActionAdded} />
                  ) : (action.notes || action.text) && (
                    <p className="text-sm text-gray-700 mt-2 bg-white p-2 rounded">
                      {action.notes || action.text}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadActionLog;
